/* eslint-disable import/no-cycle */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'

import {
  ColDef,
  GridApi,
  GridOptions,
  ICellEditorParams,
  SuppressKeyboardEventParams,
} from 'ag-grid-community'
import { produce } from 'immer'
import { flattenDeep as _flattenDeep, isNil as _isNil, set as _set } from 'lodash/fp'
import { tabbable } from 'tabbable'

import { Observable, filter, map, pairwise, shareReplay, startWith, tap } from 'rxjs'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxTableComponent } from '@flowx/angular-ui-toolkit'
import { config, localizeString } from '@flowx/core-sdk'
import {
  CollectionPrototypeInstanceMeta,
  ComponentIdentifierEnum,
  DataModelType,
  LinkTemplateConfig,
  ProcessActionTriggerEvent,
  ProcessDataModel,
  ResolvedAction,
  TableCellTemplateConfig,
  TableTemplateConfig,
  TableViewState,
  TextTemplateConfig,
  boolExpressionSync,
  captureTableViewState,
  collectionPrefix,
  expressionHasDynamicVariables,
  extractPlaceholders,
  getActionsByTemplateConfigAndTrigger$,
  getDataModelForProcess,
  getTableViewState,
  goToRestoredTablePage,
  gridColumnIds,
  gridHasRowModelRows,
  processDataValueFromPath$,
  prototypeInstanceMetaToPath,
  relativeToDataModelKey,
  reportTableRowDeleted,
  reportTableRowEdited,
  restoredGridState,
  tableColumnSourceKey,
  updateProcessData,
  updateProcessDataAtPath,
} from '@flowx/core-sdk'
import { DimensionTypes, HeightStyle, WidthStyle, applyLayoutStyles } from '@flowx/core-theme'

import { FlxTableActionsComponent } from './flx-table-actions/flx-table-actions.component'
import { FlxTableCellComponent } from './flx-table-cell/flx-table-cell.component'
import { FlxTableEditorComponent } from './flx-table-editor/flx-table-editor.component'
import * as locales from './locales'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { waitForFormControlValidation } from '../../utils/forms'
import { getBoolExpressionSignal, getComputedValueSignal, getDynamicPropSignal } from '../helpers'

const GRID_CELL_CLASSNAME = 'ag-cell'
function getAllFocusableElementsOf(el: HTMLElement): HTMLElement[] {
  return Array.from<HTMLElement>(tabbable(el) as HTMLElement[]).filter(
    (focusableEl) => focusableEl.tabIndex !== -1
  )
}

function getEventPath(event: Event): HTMLElement[] {
  const path: HTMLElement[] = []
  let currentTarget: HTMLElement | null = event.target as HTMLElement
  while (currentTarget) {
    path.push(currentTarget)
    currentTarget = currentTarget.parentElement
  }
  return path
}

/** Copied before sorting: mutating the config would churn the column-source key. */
function sortedCellConfigs(config: TableTemplateConfig): TableCellTemplateConfig[] {
  return [...config.templateConfig[0].templateConfig].sort((a, b) => a.order - b.order)
}

/**
 * Capture whether the user is tabbing forwards or backwards and suppress keyboard event if tabbing
 * outside of the children
 */
function suppressKeyboardEvent({ event, editing }: SuppressKeyboardEventParams<any>): boolean {
  const { key, shiftKey } = event

  // Block ag-grid default Enter commit while editing; commit is gated via onCellKeyDown.
  if (key === 'Enter' && editing) {
    return true
  }

  const path = getEventPath(event)
  const isTabForward = key === 'Tab' && shiftKey === false
  const isTabBackward = key === 'Tab' && shiftKey === true

  let suppressEvent = false

  // Handle cell children tabbing
  if (isTabForward || isTabBackward) {
    const eGridCell = path.find((el) => {
      if (el.classList === undefined) {
        return false
      }
      return el.classList.contains(GRID_CELL_CLASSNAME)
    })

    if (!eGridCell) {
      return suppressEvent
    }

    const focusableChildrenElements = getAllFocusableElementsOf(eGridCell)
    const lastCellChildEl = focusableChildrenElements[focusableChildrenElements.length - 1]
    const firstCellChildEl = focusableChildrenElements[0]

    // Suppress keyboard event if tabbing forward within the cell and the current focused element is not the last child
    if (focusableChildrenElements.length === 0) {
      return false
    }

    const currentIndex = focusableChildrenElements.indexOf(document.activeElement as HTMLElement)

    if (isTabForward) {
      const isLastChildFocused = lastCellChildEl && document.activeElement === lastCellChildEl

      if (!isLastChildFocused) {
        suppressEvent = true
        if (currentIndex !== -1 || document.activeElement === eGridCell) {
          event.preventDefault()
          focusableChildrenElements[currentIndex + 1].focus()
        }
      }
    }
    // Suppress keyboard event if tabbing backwards within the cell, and the current focused element is not the first child
    else {
      const isFirstChildFocused = firstCellChildEl && document.activeElement === firstCellChildEl
      const cellHasFocusedChildren =
        eGridCell.contains(document.activeElement) && eGridCell !== document.activeElement

      if (cellHasFocusedChildren) {
        if (!isFirstChildFocused) {
          suppressEvent = true
          if (currentIndex !== -1) {
            event.preventDefault()
            focusableChildrenElements[currentIndex - 1].focus()
          }
        }
      } else {
        // Manually set focus to the last child element if cell doesn't have focused children
        lastCellChildEl.focus()
        // Cancel keyboard press, so that it doesn't focus on the last child and then pass through the keyboard press to
        // move to the 2nd last child element
        event.preventDefault()
        suppressEvent = true
      }
    }
  }

  return suppressEvent
}

@Component({
  selector: 'flx-table-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [FlxTableComponent, FlxThemeModule, FlxStyleModule],
  providers: [FlxLocalizePipe],
  templateUrl: './table-wrapper.component.html',
  styleUrl: './table-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTableWrapperComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #flxLocalizePipe = inject(FlxLocalizePipe)
  #actionService = inject(FlxActionService)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  #destroyRef = inject(DestroyRef)

  #config = config()
  #locale = computed(() => this.#config.language)
  #gridApi: GridApi | undefined

  // actions for the CHANGE trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #changeActions = signal<ResolvedAction[]>([])

  // the remembered page, consumed once per grid instance
  #hoppedToPageFor: TableViewState | undefined

  #liveCellConfigs = computed(() => sortedCellConfigs(this.templateConfig()))

  // one fold per emission — the string, not the config, is what the latch below compares
  #columnSourceKey = computed(() =>
    tableColumnSourceKey(this.templateConfig(), this.#liveCellConfigs())
  )

  // holds the previous config while emissions change nothing the column defs are built from
  #columnSourceConfig = computed(() => {
    this.#columnSourceKey()
    return untracked(this.templateConfig)
  })

  #tableCellTemplateConfigs = computed(() => sortedCellConfigs(this.#columnSourceConfig()))

  #columnDefs = computed(() =>
    this.#getColumnDefs(
      this.#columnSourceConfig(),
      this.#tableCellTemplateConfigs(),
      this.dataModel()
    )
  )

  /**
   * One value per generation — `hide` destroys the grid while this component survives, so `isHidden`
   * is the generation and its only dependency; `#columnDefs` is untracked so a template change
   * cannot re-resolve this and re-arm the hop mid-session. Resolved lazily, which is what lets the
   * hop be armed while the grid is built rather than on its ready event.
   */
  #restoredViewState = computed<TableViewState | undefined>(() =>
    this.isHidden()
      ? undefined
      : getTableViewState(this.#tableViewStateSource(), gridColumnIds(untracked(this.#columnDefs)))
  )

  templateConfig = input.required<TableTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  dataModel = computed(() => getDataModelForProcess(this.#columnSourceConfig().processInstanceUuid))
  // built off the live config, not the column-source one: ag-grid reads this object only at
  // creation, so a grid re-created after a `hide` flip has to see the current height and row class
  gridOptions = computed(() => this.#getGridOptions(this.templateConfig()))

  collectionData: Signal<unknown[]>
  rowData: Signal<any[]>
  newRowValue: Record<string, any> = {}

  isHidden: Signal<boolean>

  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CHANGE)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#changeActions.set(actions))

    runInInjectionContext(this.#injector, () => {
      this.collectionData = toSignal(this.#getCollectionData(this.templateConfig()))
      this.rowData = toSignal(this.#getRowData(this.templateConfig(), this.#columnDefs()), {
        initialValue: [],
      })

      const { hide } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  onGridReady(api: GridApi): void {
    this.#gridApi = api
  }

  deleteRow(rowIndex: number): void {
    const { processInstanceUuid, prototypeInstanceMeta } = this.templateConfig()
    const source = this.#getTemplateConfigSource(this.templateConfig())
    const newCollectionData = structuredClone(this.collectionData())
    newCollectionData.splice(rowIndex, 1)
    const newValue = _set(source, newCollectionData, {})

    reportTableRowDeleted(this.templateConfig(), this.#gridApi)
    if (prototypeInstanceMeta?.length) {
      const path = prototypeInstanceMetaToPath(prototypeInstanceMeta)
      updateProcessDataAtPath(processInstanceUuid, path, newValue, { userInput: true })
    } else {
      updateProcessData(processInstanceUuid, newValue, { userInput: true })
    }
    this.#runChangeActions()
  }

  /**
   * The live config core derives the entry's address from. Read per call so an enclosing collection
   * that changed length lands on its current address; untracked because the callers are a computed
   * and grid callbacks, neither of which should take the config as a dependency.
   */
  #tableViewStateSource(): TableTemplateConfig {
    return untracked(this.templateConfig)
  }

  #runChangeActions(params?: Record<string, any>): void {
    if (this.#changeActions().length === 0) {
      return
    }
    void this.#actionService.runActions(this.#changeActions(), this.templateConfig(), params)
  }

  #getGridOptions(tableTemplateConfig: TableTemplateConfig): GridOptions {
    const source = this.#getTemplateConfigSource(tableTemplateConfig)
    const tableRowTemplateConfig = tableTemplateConfig.templateConfig[0]
    const tableCellTemplateConfigs = this.#tableCellTemplateConfigs()
    const tableCellTemplateConfigsWithWidthTypeAuto = tableCellTemplateConfigs.filter(
      ({ displayOptions }) => displayOptions.style.widthType === DimensionTypes.Auto
    )
    const tableCellWidthTypeAutoIds = tableCellTemplateConfigsWithWidthTypeAuto.map(
      (templateConfig) => this.#getFieldName(templateConfig)
    )
    const columnDefs = this.#columnDefs()
    const restoredViewState = this.#restoredViewState()
    const { heightType } = this.#getHeightStyle(tableTemplateConfig)
    const hasHeightAuto = heightType === DimensionTypes.Auto
    const pageSize = tableTemplateConfig.displayOptions.flowxProps.pageSize

    return {
      localeText: locales[this.#locale()],
      context: {
        parentComponent: this,
      },
      dataTypeDefinitions: {
        enum: {
          baseDataType: 'text',
          extendsDataType: 'text',
        },
      },
      columnDefs,
      defaultColDef: {
        autoHeight: true,
        sortable: false,
        wrapText: true,
      },
      ...(pageSize
        ? {
            pagination: true,
            paginationPageSize: pageSize,
            paginationPageSizeSelector: false,
          }
        : {}),
      ...restoredGridState(restoredViewState),
      animateRows: false,
      rowClass: tableRowTemplateConfig.displayOptions.className,
      rowHeight: 0,
      suppressMovableColumns: true,
      ...(hasHeightAuto ? { domLayout: 'autoHeight' } : {}),
      autoSizeStrategy: {
        type: 'fitCellContents',
        skipHeader: true,
        colIds: [...tableCellWidthTypeAutoIds, 'actions'],
      },
      overlayNoRowsTemplate: `<span class="ag-overlay-no-rows-center">${this.#flxLocalizePipe.transform('@@sys_table_empty')}</span>`,
      onFilterChanged: (event) => {
        if (event.api.getDisplayedRowCount() === 0) {
          event.api.showNoRowsOverlay()
        } else {
          event.api.hideOverlay()
        }
      },
      onStateUpdated: (event) => captureTableViewState(this.#tableViewStateSource(), event),
      // The other half of the split restore. An out-of-range page is left to
      // `paginationGoToPage`, which clamps to the last.
      onModelUpdated: ({ api }) => {
        const restored = untracked(this.#restoredViewState)
        if (!restored || this.#hoppedToPageFor === restored || !gridHasRowModelRows(api)) {
          return
        }

        this.#hoppedToPageFor = restored
        goToRestoredTablePage(api, restored.page)
      },
      editType: 'fullRow',
      onCellKeyDown: async (params) => {
        const keyboardEvent = params.event as KeyboardEvent | null
        if (!keyboardEvent || keyboardEvent.key !== 'Enter') {
          return
        }
        if (!params.api.getEditingCells().length) {
          return
        }

        const instances = params.api.getCellEditorInstances() as FlxTableEditorComponent[]
        await Promise.all(
          instances.map((instance) =>
            instance.formControl
              ? waitForFormControlValidation(instance.formControl)
              : Promise.resolve()
          )
        )
        if (instances.every((instance) => instance.formControl?.valid)) {
          params.api.stopEditing()
        }
      },
      onRowValueChanged: (event) => {
        const rowIndex = event.node.sourceRowIndex
        const newCollectionData = produce(this.collectionData() as any[], (draft) => {
          Object.entries(this.newRowValue).forEach(([key, value]) => {
            draft[rowIndex] = _set(key, value, draft[rowIndex])
          })
        })
        const newValue = _set(source, newCollectionData, {})

        if (tableTemplateConfig.prototypeInstanceMeta?.length) {
          const path = prototypeInstanceMetaToPath(tableTemplateConfig.prototypeInstanceMeta)
          updateProcessDataAtPath(tableTemplateConfig.processInstanceUuid, path, newValue, {
            userInput: true,
          })
        } else {
          updateProcessData(tableTemplateConfig.processInstanceUuid, newValue, {
            userInput: true,
          })
        }
        reportTableRowEdited(this.templateConfig())
        this.#runChangeActions({ component: this.templateConfig().componentIdentifier })
        this.newRowValue = {}
      },
    }
  }

  #getFieldName(templateConfig: TableCellTemplateConfig): string {
    return templateConfig.flowxUuid.toString()
  }

  #getTemplateConfigSource(templateConfig: TableTemplateConfig): string {
    return templateConfig.displayOptions.flowxProps.source
  }

  #getHeightStyle(templateConfig: TableTemplateConfig): HeightStyle {
    const { heightType, height } = templateConfig.displayOptions.style
    return { heightType, height }
  }

  #getWidthStyle(
    templateConfig: TableCellTemplateConfig
  ): WidthStyle & { minWidth: number | null; maxWidth: number | null } {
    const { widthType, width, minWidth, maxWidth } = templateConfig.displayOptions.style
    return { widthType, width, minWidth, maxWidth }
  }

  #getColumnDefs(
    tableTemplateConfig: TableTemplateConfig,
    tableCellTemplateConfigs: TableCellTemplateConfig[],
    dataModel: ProcessDataModel
  ): ColDef[] {
    const tableCellColDefs = this.#mapTableCellTemplateConfigsToColDefs(
      tableTemplateConfig,
      tableCellTemplateConfigs,
      dataModel
    )
    const actionsColumnDef: ColDef = {
      headerName: '',
      field: 'actions',
      cellRenderer: FlxTableActionsComponent,
      cellRendererParams: {
        tableTemplateConfig,
      },
      valueFormatter: () => null,
      resizable: false,
      pinned: 'right',
      suppressKeyboardEvent,
    }
    const hasEditableColumns = tableCellColDefs.some((colDef) => colDef.editable)
    const hasDeletableRows = tableTemplateConfig.displayOptions.flowxProps.deletableRows
    const hasActionsColumn = hasEditableColumns || hasDeletableRows
    return [...tableCellColDefs, ...(hasActionsColumn ? [actionsColumnDef] : [])]
  }

  #mapTableCellTemplateConfigsToColDefs(
    tableTemplateConfig: TableTemplateConfig,
    tableCellTemplateConfigs: TableCellTemplateConfig[],
    dataModel: ProcessDataModel
  ): ColDef[] {
    return tableCellTemplateConfigs.map((tableCelltemplateConfig, index) =>
      this.#mapTemplateConfigToColDef(
        tableTemplateConfig,
        tableCelltemplateConfig,
        dataModel,
        index
      )
    )
  }

  #mapTemplateConfigToColDef(
    tableTemplateConfig: TableTemplateConfig,
    tableCellTemplateConfig: TableCellTemplateConfig,
    dataModel: ProcessDataModel,
    index: number
  ): ColDef {
    const field = this.#getFieldName(tableCellTemplateConfig)
    const widthStyle = this.#getWidthStyle(tableCellTemplateConfig)
    const resizable = tableCellTemplateConfig.displayOptions.style.resizable
    const titleAlignment = tableCellTemplateConfig.displayOptions.style.titleAlignment
    const sortable = tableCellTemplateConfig.displayOptions.flowxProps.sortable
    const defaultSorting = tableTemplateConfig.displayOptions.flowxProps.defaultSorting
    const hasDefaultSorting = tableCellTemplateConfig.flowxUuid === defaultSorting?.column
    const filterable = tableCellTemplateConfig.displayOptions.flowxProps.filterable
    const editable = tableCellTemplateConfig.displayOptions.flowxProps.editable

    const source = this.#getTemplateConfigSource(tableTemplateConfig)
    const dataModelSource = tableTemplateConfig.prototypeInstanceMeta
      ? relativeToDataModelKey(tableTemplateConfig.prototypeInstanceMeta, source)
      : source
    const templateConfigsWithText = this.#getTemplateConfigWithTextChildren(tableCellTemplateConfig)
    const templateConfigsWithTextWithCurrencyAmountKeyPaths =
      this.#replaceTemplateConfigsCurrencyKeyPathsWithCurrencyAmountKeyPaths(
        templateConfigsWithText,
        dataModel,
        dataModelSource
      )
    const templateConfigsWithTextNotComputed =
      templateConfigsWithTextWithCurrencyAmountKeyPaths.filter(
        (templateConfig) => !templateConfig.displayOptions.flowxProps.isComputedText
      )
    const firstTemplateConfigWithDynamicText = templateConfigsWithTextNotComputed.find(
      (templateConfig) =>
        expressionHasDynamicVariables(templateConfig?.displayOptions?.flowxProps.text)
    )
    let cellDataType = 'text'
    let isEditableEnabled = false
    let dataModelKeyPath = ''

    if (firstTemplateConfigWithDynamicText) {
      const { text } = firstTemplateConfigWithDynamicText.displayOptions.flowxProps
      const keyPaths = extractPlaceholders(text)
      dataModelKeyPath = `${dataModelSource}.${keyPaths[0]}`
      const keyDataModel = dataModel[dataModelKeyPath]
      const keyDataModelType = keyDataModel?.type
      cellDataType = this.#mapKeyDataModelTypeToCellDataType(keyDataModelType)
      const keyIsLinkedToDataModel = !!keyDataModel
      const isTypeSupported = ![DataModelType.OBJECT].includes(keyDataModelType)
      isEditableEnabled = editable && keyIsLinkedToDataModel && isTypeSupported
    }

    const isEditableHidden = (
      rowData: Record<string, CollectionPrototypeInstanceMeta[]>
    ): boolean => {
      if (!firstTemplateConfigWithDynamicText || !rowData) {
        return false
      }

      const prototypeInstanceMeta = rowData[field]
      if (!prototypeInstanceMeta) {
        return false
      }

      return boolExpressionSync(
        firstTemplateConfigWithDynamicText.processInstanceUuid,
        prototypeInstanceMeta,
        firstTemplateConfigWithDynamicText.displayOptions.flowxProps?.hide || ''
      )
    }

    return {
      colId: `${tableCellTemplateConfig.flowxUuid}-${index}`,
      headerName: localizeString(tableCellTemplateConfig.displayOptions.flowxProps.label),
      ...(titleAlignment ? { headerClass: `ag-${titleAlignment}-aligned-header` } : {}),
      field,
      cellRenderer: FlxTableCellComponent,
      cellRendererParams: {
        config: {
          ...tableCellTemplateConfig,
          flowxUuid: `${tableCellTemplateConfig.flowxUuid}-${index}-${tableTemplateConfig.flowxUuid}`,
        } as TableCellTemplateConfig,
      },
      cellEditor: FlxTableEditorComponent,
      cellEditorParams: {
        tableTemplateConfig,
        tableCellTemplateConfig,
        firstTemplateConfigWithDynamicText,
        dataModelKeyPath,
      },
      ...(widthStyle.widthType === DimensionTypes.Fill ? { flex: 1 } : {}),
      ...(widthStyle.widthType === DimensionTypes.Fixed ? { width: widthStyle.width || 0 } : {}),
      cellClass: [
        tableCellTemplateConfig.displayOptions.className,
        applyLayoutStyles(tableCellTemplateConfig.displayOptions.style),
      ]
        .filter(Boolean)
        .join(' '),
      resizable,
      suppressKeyboardEvent,
      ...(!_isNil(widthStyle.minWidth) ? { minWidth: widthStyle.minWidth, flex: 1 } : {}),
      ...(!_isNil(widthStyle.maxWidth) ? { maxWidth: widthStyle.maxWidth } : {}),
      ...(sortable
        ? {
            sortable: true,
            ...(hasDefaultSorting ? { sort: defaultSorting.direction } : {}),
          }
        : {}),
      ...(filterable
        ? {
            filter: true,
            filterParams: {
              buttons: ['reset'],
            },
          }
        : {}),
      ...(isEditableEnabled
        ? {
            editable: (params: ICellEditorParams) => !isEditableHidden(params.data),
          }
        : {}),
      cellDataType,
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const prototypeInstanceMetaA: CollectionPrototypeInstanceMeta[] = nodeA.data[field]
        const prototypeInstanceMetaB: CollectionPrototypeInstanceMeta[] = nodeB.data[field]

        const sortingTemplateConfigSegmentsA = this.#extractSortingSegmentsFromTemplateConfigs(
          templateConfigsWithTextWithCurrencyAmountKeyPaths,
          prototypeInstanceMetaA,
          dataModelSource,
          dataModel
        )
        const sortingTemplateConfigSegmentsB = this.#extractSortingSegmentsFromTemplateConfigs(
          templateConfigsWithTextWithCurrencyAmountKeyPaths,
          prototypeInstanceMetaB,
          dataModelSource,
          dataModel
        )

        for (let i = 0; i < sortingTemplateConfigSegmentsA.length; i++) {
          const segmentA = sortingTemplateConfigSegmentsA[i]
          const segmentB = sortingTemplateConfigSegmentsB[i]

          if (segmentA !== segmentB) {
            if (typeof segmentA === 'number' && typeof segmentB === 'number') {
              if (!Number.isFinite(segmentA)) {
                return 1 // 'a' is NaN, comes last
              }
              if (!Number.isFinite(segmentB)) {
                return -1 // 'b' is NaN, comes last
              }
              return segmentA - segmentB
            }

            if (!segmentA) {
              return 1 // 'a' is empty, comes last
            }
            if (!segmentB) {
              return -1 // 'b' is empty, comes last
            }

            return (segmentA as string).localeCompare(segmentB as string, undefined, {
              numeric: true,
            })
          }
        }

        return 0
      },
      filterValueGetter: (params) => {
        const prototypeInstanceMeta: CollectionPrototypeInstanceMeta[] = params.data[field]
        if (cellDataType !== 'text') {
          const filterTemplateConfig = this.#createTemplateConfigWithFirstKeyOnly(
            firstTemplateConfigWithDynamicText,
            prototypeInstanceMeta
          )
          const filterValue = this.#getTextValue(filterTemplateConfig, true)
          switch (cellDataType) {
            case 'number': {
              const num = parseFloat(filterValue)
              return isNaN(num) ? null : num
            }
            case 'date': {
              const date = new Date(filterValue)
              if (isNaN(date.getTime())) {
                return null
              }
              return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
            }
            case 'boolean':
              return this.#transformToBoolean(filterValue)
          }
        }
        return this.#getCombinedTextValue(templateConfigsWithText, prototypeInstanceMeta)
      },
      valueGetter: (params) => {
        if (isEditableEnabled) {
          const prototypeInstanceMeta: CollectionPrototypeInstanceMeta[] = params.data[field]
          const valueGetterTemplateConfig = this.#createTemplateConfigWithFirstKeyOnly(
            firstTemplateConfigWithDynamicText,
            prototypeInstanceMeta
          )
          const value = this.#getTextValue(valueGetterTemplateConfig, true)
          return value
        }
        return null
      },
      valueSetter: (params) => {
        if (
          cellDataType === 'date' &&
          this.#isValidDate(params.newValue) &&
          this.#isValidDate(params.oldValue) &&
          new Date(params.newValue).toISOString() === new Date(params.oldValue).toISOString()
        ) {
          return false
        }
        const { text } = firstTemplateConfigWithDynamicText.displayOptions.flowxProps
        const keyPaths = extractPlaceholders(text)
        const firstKeyPath = keyPaths[0]
        this.newRowValue[firstKeyPath] =
          cellDataType === 'date' && params.newValue && this.#isValidDate(params.newValue)
            ? new Date(params.newValue).toISOString()
            : params.newValue
        return true
      },
    }
  }

  #getRowData(tableTemplateConfig: TableTemplateConfig, columnDefs: ColDef[]): Observable<any[]> {
    return this.#getCollectionData(tableTemplateConfig).pipe(
      tap(() => this.#gridApi?.onFilterChanged()),
      startWith([]),
      pairwise(),
      filter(([prev, curr]) => prev?.length !== curr?.length),
      map(([, curr]) => curr),
      map((collectionData) =>
        this.#mapCollectionDataToRowData(tableTemplateConfig, collectionData, columnDefs)
      )
    )
  }

  #getCollectionData({
    processInstanceUuid,
    displayOptions: {
      flowxProps: { source },
    },
    prototypeInstanceMeta,
  }: TableTemplateConfig): Observable<unknown[]> {
    return processDataValueFromPath$(
      processInstanceUuid,
      collectionPrefix(source, prototypeInstanceMeta)
    ).pipe(
      filter((collectionData): collectionData is unknown[] => !!collectionData),
      shareReplay(1)
    )
  }

  #mapCollectionDataToRowData(
    tableTemplateConfig: TableTemplateConfig,
    collectionData: unknown[],
    columnDefs: ColDef[]
  ): unknown[] {
    const columnFields = columnDefs.map(({ field }) => field)
    const source = this.#getTemplateConfigSource(tableTemplateConfig)
    const parentPrototypeInstanceMeta = tableTemplateConfig.prototypeInstanceMeta ?? []

    return collectionData.map((_, index) =>
      columnFields.reduce((rowData, field) => {
        rowData[field] = [
          ...parentPrototypeInstanceMeta,
          {
            index,
            collectionPath: source,
          },
        ]
        return rowData
      }, {})
    )
  }

  #getTemplateConfigWithTextChildren(
    templateConfig: TableCellTemplateConfig
  ): (TextTemplateConfig | LinkTemplateConfig)[] {
    // copied before sorting: mutating the config would churn the column-source key
    return [...templateConfig.templateConfig]
      .sort((a, b) => a.order - b.order)
      .filter((templateConfig): templateConfig is TextTemplateConfig | LinkTemplateConfig =>
        [ComponentIdentifierEnum.TEXT, ComponentIdentifierEnum.LINK].includes(
          templateConfig.componentIdentifier
        )
      )
  }

  #replaceTemplateConfigsCurrencyKeyPathsWithCurrencyAmountKeyPaths(
    templateConfigs: (TextTemplateConfig | LinkTemplateConfig)[],
    dataModel: ProcessDataModel,
    source: string
  ): (TextTemplateConfig | LinkTemplateConfig)[] {
    return templateConfigs.map((templateConfigWithText) =>
      this.#replaceTemplateConfigCurrencyKeyPathsWithCurrencyAmountKeyPaths(
        templateConfigWithText,
        dataModel,
        source
      )
    )
  }

  #replaceTemplateConfigCurrencyKeyPathsWithCurrencyAmountKeyPaths(
    templateConfig: TextTemplateConfig | LinkTemplateConfig,
    dataModel: ProcessDataModel,
    source: string
  ): TextTemplateConfig | LinkTemplateConfig {
    const { text, isComputedText } = templateConfig.displayOptions.flowxProps
    if (isComputedText) {
      return templateConfig
    }
    const keyPaths = extractPlaceholders(text)
    const currenctKeyPaths = keyPaths.filter((keyPath) => {
      const dataModelKeyPath = `${source}.${keyPath}`
      const keyDataModel = dataModel[dataModelKeyPath]
      const keyDataModelType = keyDataModel?.type
      return keyDataModelType === DataModelType.CURRENCY
    })
    if (!currenctKeyPaths.length) {
      return templateConfig
    }
    return produce(templateConfig, (draft) => {
      currenctKeyPaths.forEach((currencyKeyPath) => {
        const regex = new RegExp(`\\$\\{${currencyKeyPath}\\}`, 'g')
        draft.displayOptions.flowxProps.text =
          templateConfig.displayOptions.flowxProps.text.replace(
            regex,
            `\${${currencyKeyPath}.amount}`
          )
      })
    })
  }

  #mapKeyDataModelTypeToCellDataType(
    keyDataModelType: DataModelType
  ): 'text' | 'number' | 'boolean' | 'date' | 'enum' {
    switch (keyDataModelType) {
      case DataModelType.INTEGER:
      case DataModelType.FLOAT:
      case DataModelType.CURRENCY:
        return 'number'
      case DataModelType.BOOLEAN:
        return 'boolean'
      case DataModelType.DATE_TIME:
      case DataModelType.ZONED_DATE_TIME:
        return 'date'
      case DataModelType.ENUM:
        return 'enum'
      default:
        return 'text'
    }
  }

  #createTemplateConfigWithFirstKeyOnly(
    templateConfig: TextTemplateConfig | LinkTemplateConfig,
    prototypeInstanceMeta: CollectionPrototypeInstanceMeta[]
  ): TextTemplateConfig | LinkTemplateConfig {
    const { text } = templateConfig.displayOptions.flowxProps
    const keyPaths = extractPlaceholders(text)
    const keyOnlyText = this.#getKeyOnlyText(keyPaths[0])
    return produce(templateConfig, (draft) => {
      draft.displayOptions.flowxProps.text = keyOnlyText
      draft.prototypeInstanceMeta = prototypeInstanceMeta
    })
  }

  #getCombinedTextValue(
    templateConfigs: (TextTemplateConfig | LinkTemplateConfig)[],
    prototypeInstanceMeta: CollectionPrototypeInstanceMeta[],
    skipLocalization?: boolean
  ): string {
    return templateConfigs
      .map((templateConfig) =>
        this.#getTextValue({ ...templateConfig, prototypeInstanceMeta }, skipLocalization)
      )
      .join(' ')
  }

  #getTextValue(
    templateConfig: TextTemplateConfig | LinkTemplateConfig,
    skipLocalization = false
  ): string {
    const { text, isComputedText } = templateConfig.displayOptions.flowxProps
    return runInInjectionContext(this.#injector, () =>
      (isComputedText
        ? getComputedValueSignal(text, templateConfig)
        : getDynamicPropSignal(text, templateConfig, skipLocalization))()
    )
  }

  #transformToBoolean(value: any): boolean | null {
    const trueValues = [true, 'true', 1, '1']
    const falseValues = [false, 'false', 0, '0']
    return trueValues.includes(value) ? true : falseValues.includes(value) ? false : null
  }

  #isValidDate(value: any): boolean {
    const date = new Date(value)
    return !isNaN(date.getTime())
  }

  #getKeyOnlyText(text: string): string {
    return `\${${text}}`
  }

  #getKeyDataModelType(
    keyPath: string,
    source: string,
    dataModel: ProcessDataModel
  ): DataModelType | undefined {
    const dataModelKeyPath = `${source}.${keyPath}`
    const keyDataModel = dataModel[dataModelKeyPath]
    return keyDataModel?.type
  }

  #extractSortingSegmentsFromTemplateConfigs(
    templateConfigs: (TextTemplateConfig | LinkTemplateConfig)[],
    prototypeInstanceMeta: CollectionPrototypeInstanceMeta[],
    source: string,
    dataModel: ProcessDataModel
  ): (string | number)[] {
    return _flattenDeep(
      templateConfigs.map((templateConfig) =>
        this.#extractSortingSegmentsFromTemplateConfig(
          {
            ...templateConfig,
            prototypeInstanceMeta,
          },
          source,
          dataModel
        )
      )
    )
  }

  #extractSortingSegmentsFromTemplateConfig(
    templateConfig: TextTemplateConfig | LinkTemplateConfig,
    source: string,
    dataModel: ProcessDataModel
  ): (string | number)[] {
    return runInInjectionContext(this.#injector, () => {
      const { text, isComputedText } = templateConfig.displayOptions.flowxProps
      if (isComputedText) {
        return [getComputedValueSignal(text, templateConfig)()]
      }
      const keyPaths = extractPlaceholders(text)
      return keyPaths.map((keyPath) => {
        const keyOnlyText = this.#getKeyOnlyText(keyPath)
        const keyDataModelType = this.#getKeyDataModelType(keyPath, source, dataModel)
        const value = getDynamicPropSignal(keyOnlyText, templateConfig, true)()
        const isNumericValue = [DataModelType.INTEGER, DataModelType.FLOAT].includes(
          keyDataModelType
        )
        return isNumericValue ? parseFloat(value) : value
      })
    })
  }
}
